/**
 * React Query hooks for the landing/about admin CMS (ata #34).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from './client';
import type {
  CmsPageResponse,
  CmsSectionResponse,
  CmsSectionVersionResponse,
  CreateCmsSectionRequest,
  PublicCmsSectionResponse,
  ReorderSectionsRequest,
  UpdateCmsSectionRequest,
} from './cmsTypes';

const cmsKeys = {
  all: ['cms'] as const,
  public: (slug: string, locale?: string) => [...cmsKeys.all, 'public', slug, locale] as const,
  pages: ['cms', 'admin', 'pages'] as const,
  sections: (slug: string) => ['cms', 'admin', 'sections', slug] as const,
  section: (id: number) => ['cms', 'admin', 'section', id] as const,
  versions: (id: number) => ['cms', 'admin', 'section', id, 'versions'] as const,
};

// ---- Public -----------------------------------------------------------------

export function usePublicCmsSections(slug: string, locale?: string) {
  return useQuery({
    queryKey: cmsKeys.public(slug, locale),
    queryFn: async () => {
      const params = locale ? { locale } : undefined;
      const { data } = await axiosInstance.get<PublicCmsSectionResponse[]>(`/cms/pages/${slug}/public`, { params });
      return data;
    },
  });
}

// ---- Admin ------------------------------------------------------------------

export function useCmsPages() {
  return useQuery({
    queryKey: cmsKeys.pages,
    queryFn: async () => {
      const { data } = await axiosInstance.get<CmsPageResponse[]>('/admin/cms/pages');
      return data;
    },
  });
}

export function useCmsSections(slug: string | null) {
  return useQuery({
    queryKey: cmsKeys.sections(slug ?? ''),
    queryFn: async () => {
      const { data } = await axiosInstance.get<CmsSectionResponse[]>(`/admin/cms/pages/${slug}/sections`);
      return data;
    },
    enabled: slug !== null,
  });
}

export function useCreateCmsSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateCmsSectionRequest) => {
      const { data } = await axiosInstance.post<CmsSectionResponse>(`/admin/cms/pages/${slug}/sections`, req);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function useUpdateCmsSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...req }: UpdateCmsSectionRequest & { id: number }) => {
      const { data } = await axiosInstance.put<CmsSectionResponse>(`/admin/cms/sections/${id}`, req);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function usePublishCmsSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.patch<CmsSectionResponse>(`/admin/cms/sections/${id}/publish`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function useSetCmsSectionActive(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      const { data } = await axiosInstance.patch<CmsSectionResponse>(`/admin/cms/sections/${id}/active`, null, {
        params: { active },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function useDeleteCmsSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`/admin/cms/sections/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function useReorderCmsSections(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: ReorderSectionsRequest) => {
      await axiosInstance.put(`/admin/cms/pages/${slug}/sections/reorder`, req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) }),
  });
}

export function useCmsSectionVersions(sectionId: number | null) {
  return useQuery({
    queryKey: cmsKeys.versions(sectionId ?? 0),
    queryFn: async () => {
      const { data } = await axiosInstance.get<CmsSectionVersionResponse[]>(`/admin/cms/sections/${sectionId}/versions`);
      return data;
    },
    enabled: sectionId !== null,
  });
}

/** Uploads an inline image for use inside a section's rich-text body (ata #34, plan Fase D). */
export function useUploadCmsMedia() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axiosInstance.post<{ url: string }>('/admin/cms/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

export function useRevertCmsSection(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, versionId }: { id: number; versionId: number }) => {
      const { data } = await axiosInstance.post<CmsSectionResponse>(`/admin/cms/sections/${id}/versions/${versionId}/revert`);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: cmsKeys.sections(slug) });
      qc.invalidateQueries({ queryKey: cmsKeys.versions(variables.id) });
    },
  });
}
